import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import '../../assets/styles/infrastructure_leasing_modal.scss';
import CommonService from '../../services/common.service';
import {
  postSellerEnquiryRequirementQuotation,
  fetchPublishedInfrastructureById,
  dispatchInfrastructureNotificationsRefresh,
  fetchSellerEnquiryBuyerRequirement,
  rejectInfrastructureListingEnquiry,
} from '../../services/infrastructureLeasingListing.service';
import {
  parseLeaseRangeFromText,
  buildAvailabilityContext,
  buildBuyerRequirementContext,
  buildQuotationTimelineLine,
  computeInclusiveEndFromStartAndUnits,
  durationTypeLabel,
  getRequirementQuotationValidationFlags,
  inferLeaseUnitsFromRange,
  validateSellerBuyerLeaseRange,
} from '../../utils/infrastructureListingAvailability';
import InfrastructureLeasingListingAvailabilityStep from './InfrastructureLeasingListingAvailabilityStep';
import InfrastructureLeasingDealRejectModal from './InfrastructureLeasingDealRejectModal';
import { useCurrencyMaster } from '../../hooks/useCurrencyMaster';
import { useFactoryDetailsMaster } from '../../hooks/useFactoryDetailsMaster';

const ASSET_CLASS_LABELS = {
  commercial_property: 'Commercial Property',
  warehouse: 'Warehouse',
  factory_industrial: 'Factory / Industrial Land',
  industrial_shed: 'Industrial Shed',
  machinery: 'Machinery',
  equipment_infra: 'Equipment & Infrastructure',
};

function str(v) {
  if (v == null) return '';
  return String(v).trim();
}

function parseQuotedPriceParts(full) {
  const s = str(full);
  if (!s) return { amount: '', currency: 'INR' };
  const parts = s.trim().split(/\s+/);
  if (parts.length >= 2) {
    const cur = parts[parts.length - 1];
    if (/^[A-Z]{3}$/i.test(cur)) {
      return { amount: parts.slice(0, -1).join(' '), currency: cur.toUpperCase() };
    }
  }
  return { amount: s, currency: 'INR' };
}

function InfoItem({ label, value }) {
  if (value == null || str(value) === '') return null;
  return (
    <div className="col-md-6 mb-2">
      <div className="small text-muted mb-0">{label}</div>
      <div className="small fw-semibold text-break">{value}</div>
    </div>
  );
}

/**
 * Full seller quotation flow: read-only context, calendar validation, commercial fields, optional upload.
 */
export default function InfrastructureLeasingRequirementQuotationModal({
  open,
  onClose,
  listingId,
  enquiryId,
  buyerName: buyerNameProp,
  listingTitle: listingTitleProp,
  enquiry,
  existingQuotation,
  onSuccess,
}) {
  const [listingForm, setListingForm] = useState(null);
  const [listingInventoryAvailability, setListingInventoryAvailability] = useState(null);
  const [listingLoading, setListingLoading] = useState(false);
  const [listingError, setListingError] = useState(null);
  const [buyerRequirementForm, setBuyerRequirementForm] = useState(null);
  const [buyerRequirementMeta, setBuyerRequirementMeta] = useState(null);
  const [buyerRequirementLoading, setBuyerRequirementLoading] = useState(false);
  const [buyerRequirementError, setBuyerRequirementError] = useState(null);
  const [rangeValue, setRangeValue] = useState(null);

  const [quotedAmount, setQuotedAmount] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [securityDeposit, setSecurityDeposit] = useState('');
  const [additionalCharges, setAdditionalCharges] = useState('');
  const [messageToBuyer, setMessageToBuyer] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [quotationDocPath, setQuotationDocPath] = useState(null);
  const [quotationDocName, setQuotationDocName] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectComments, setRejectComments] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const suffix = `${listingId || 'x'}-${enquiryId || 'y'}`;
  const didApplyDefaultRangeRef = useRef(false);

  const { currencyOptions, loading: currencyMasterLoading } = useCurrencyMaster();
  const { paymentScheduleOptions, loading: paymentTermsMasterLoading } = useFactoryDetailsMaster();

  const currencySelectList = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const o of currencyOptions || []) {
      const code = str(o.currencyCode || o.code || '');
      const val = code || str(o.value);
      if (!val || seen.has(val)) continue;
      seen.add(val);
      out.push({ value: val, label: str(o.label) || val });
    }
    return out;
  }, [currencyOptions]);

  const paymentTermsMatchesMaster = useMemo(() => {
    const p = str(paymentTerms);
    if (!p) return '';
    return paymentScheduleOptions.some((o) => str(o.label) === p) ? p : '';
  }, [paymentTerms, paymentScheduleOptions]);

  useEffect(() => {
    if (!open) setRejectModalOpen(false);
  }, [open]);

  useEffect(() => {
    if (!rejectModalOpen) return;
    setRejectReason('');
    setRejectComments('');
    setRejectSubmitting(false);
  }, [rejectModalOpen]);

  const buyerName = str(enquiry?.buyerName) || str(buyerNameProp);
  const listingTitleDisplay = str(listingForm?.listingTitle) || str(listingTitleProp) || str(enquiry?.assetName);
  const requirementIdDisplay =
    buyerRequirementMeta?.id != null && Number.isFinite(Number(buyerRequirementMeta.id))
      ? String(buyerRequirementMeta.id)
      : enquiry?.buyerRequirementId != null
        ? String(enquiry.buyerRequirementId)
        : '—';
  const locationDisplay =
    str(buyerRequirementMeta?.locationLabel) ||
    str(enquiry?.assetLocation) ||
    str(listingForm?.addressLine1 || listingForm?.cityName || listingForm?.locationDetails) ||
    '—';
  const assetClassKey = str(listingForm?.assetClass) || str(enquiry?.assetType);
  const assetTypeDisplay = ASSET_CLASS_LABELS[assetClassKey] || assetClassKey || '—';

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const q = existingQuotation;
    const ext = q?.extended && typeof q.extended === 'object' ? q.extended : {};
    const parsed = parseQuotedPriceParts(q?.quotedPrice);
    setQuotedAmount(ext.quotedAmount != null ? str(ext.quotedAmount) : parsed.amount);
    setCurrency(str(ext.currency) || parsed.currency || 'INR');
    setPaymentTerms(str(ext.paymentTerms));
    setSecurityDeposit(str(ext.securityDeposit));
    setAdditionalCharges(str(ext.additionalCharges));
    setMessageToBuyer(q?.additionalNotes != null ? String(q.additionalNotes) : '');
    setTermsAndConditions(str(ext.termsAndConditions));
    setQuotationDocPath(ext.quotationDocumentPath != null ? String(ext.quotationDocumentPath) : null);
    setQuotationDocName(ext.quotationDocumentName != null ? String(ext.quotationDocumentName) : null);
    setSubmitting(false);
    const parsedRange = parseLeaseRangeFromText(q?.timelineAvailability);
    setRangeValue(parsedRange && parsedRange[0] && parsedRange[1] ? [parsedRange[0], parsedRange[1]] : null);
    didApplyDefaultRangeRef.current = false;
    return undefined;
  }, [open, listingId, enquiryId, existingQuotation]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const lid = listingId == null ? NaN : Number(listingId);
    const eid = enquiryId == null ? NaN : Number(enquiryId);
    if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
      setListingError('Invalid listing or enquiry.');
      setListingForm(null);
      return undefined;
    }
    let cancelled = false;
    setListingLoading(true);
    setListingError(null);
    setListingForm(null);
    setListingInventoryAvailability(null);
    (async () => {
      try {
        const res = await fetchPublishedInfrastructureById(lid);
        if (cancelled) return;
        if (res?.success === false || !res?.data) {
          setListingError(res?.message || 'Listing could not be loaded.');
          return;
        }
        setListingForm(res.data.form || {});
        setListingInventoryAvailability(res.data.inventoryAvailability ?? null);
      } catch (e) {
        if (!cancelled) {
          setListingError(e?.response?.data?.message || e?.message || 'Failed to load listing.');
        }
      } finally {
        if (!cancelled) setListingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, listingId, enquiryId]);

  useEffect(() => {
    if (!open) {
      setBuyerRequirementForm(null);
      setBuyerRequirementMeta(null);
      setBuyerRequirementError(null);
      setBuyerRequirementLoading(false);
      return undefined;
    }
    const lid = listingId == null ? NaN : Number(listingId);
    const eid = enquiryId == null ? NaN : Number(enquiryId);
    if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
      setBuyerRequirementError('Invalid enquiry.');
      setBuyerRequirementForm(null);
      setBuyerRequirementMeta(null);
      return undefined;
    }
    let cancelled = false;
    setBuyerRequirementLoading(true);
    setBuyerRequirementError(null);
    setBuyerRequirementForm(null);
    setBuyerRequirementMeta(null);
    (async () => {
      try {
        const res = await fetchSellerEnquiryBuyerRequirement(lid, eid);
        if (cancelled) return;
        if (res?.success === false || !res?.data?.form) {
          setBuyerRequirementError(res?.message || 'Buyer requirement could not be loaded.');
          return;
        }
        setBuyerRequirementForm(res.data.form);
        setBuyerRequirementMeta({
          id: res.data.id,
          locationLabel: res.data.locationLabel != null ? String(res.data.locationLabel) : '',
        });
      } catch (e) {
        if (!cancelled) {
          setBuyerRequirementError(e?.response?.data?.message || e?.message || 'Failed to load buyer requirement.');
        }
      } finally {
        if (!cancelled) setBuyerRequirementLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, listingId, enquiryId]);

  useEffect(() => {
    if (!open || !listingForm || listingLoading || buyerRequirementLoading) {
      return undefined;
    }
    if (didApplyDefaultRangeRef.current) {
      return undefined;
    }
    const parsed = parseLeaseRangeFromText(existingQuotation?.timelineAvailability);
    if (parsed?.[0] && parsed?.[1]) {
      didApplyDefaultRangeRef.current = true;
      return undefined;
    }
    const sellerCtx = buildAvailabilityContext(listingForm, {
      inventoryAvailability: listingInventoryAvailability || undefined,
    });
    const buyerCtx = buildBuyerRequirementContext(buyerRequirementForm || {});
    let units = buyerCtx.maxDuration ?? buyerCtx.minDuration ?? sellerCtx.minTenure ?? 1;
    if (!Number.isFinite(units) || units <= 0) units = 1;

    let start = dayjs().startOf('day');
    if (start.isBefore(sellerCtx.availableFrom.startOf('day'))) {
      start = sellerCtx.availableFrom.clone().startOf('day');
    }
    let end = computeInclusiveEndFromStartAndUnits(start, units, sellerCtx.durationType);
    if (!end?.isValid?.()) {
      didApplyDefaultRangeRef.current = true;
      return undefined;
    }
    if (end.isAfter(sellerCtx.windowEndInclusive.startOf('day'))) {
      end = sellerCtx.windowEndInclusive.clone().startOf('day');
    }

    const chk = validateSellerBuyerLeaseRange(start, end, sellerCtx, buyerCtx);
    if (!chk.ok) {
      let low = buyerCtx.minDuration ?? units;
      if (!Number.isFinite(low) || low <= 0) low = 1;
      end = computeInclusiveEndFromStartAndUnits(start, Math.min(low, units), sellerCtx.durationType);
      if (end?.isAfter(sellerCtx.windowEndInclusive)) {
        end = sellerCtx.windowEndInclusive.clone().startOf('day');
      }
    }

    setRangeValue([start, end]);
    didApplyDefaultRangeRef.current = true;
    return undefined;
  }, [
    open,
    listingForm,
    listingInventoryAvailability,
    listingLoading,
    buyerRequirementLoading,
    buyerRequirementForm,
    existingQuotation,
  ]);

  const sellerCtxForLease = useMemo(
    () =>
      buildAvailabilityContext(listingForm || {}, {
        inventoryAvailability: listingInventoryAvailability || undefined,
      }),
    [listingForm, listingInventoryAvailability]
  );
  const buyerCtxForVal = useMemo(() => buildBuyerRequirementContext(buyerRequirementForm || {}), [buyerRequirementForm]);

  const selectionCheck = useMemo(() => {
    if (!listingForm) {
      return { ok: false, messages: ['Listing not loaded.'], warnings: [] };
    }
    const pair = Array.isArray(rangeValue) ? rangeValue : null;
    const start = pair?.[0];
    const end = pair?.[1];
    const sellerCtx = buildAvailabilityContext(listingForm, {
      inventoryAvailability: listingInventoryAvailability || undefined,
    });
    const buyerCtx = buildBuyerRequirementContext(buyerRequirementForm || {});
    if (!start?.isValid?.() || !end?.isValid?.()) {
      return { ok: false, messages: ['Choose lease dates in the calendar.'], warnings: [] };
    }
    return validateSellerBuyerLeaseRange(start, end, sellerCtx, buyerCtx);
  }, [rangeValue, listingForm, listingInventoryAvailability, buyerRequirementForm]);

  const validationFlags = useMemo(() => {
    const pair = Array.isArray(rangeValue) ? rangeValue : null;
    const start = pair?.[0];
    const end = pair?.[1];
    const sellerCtx = buildAvailabilityContext(listingForm || {}, {
      inventoryAvailability: listingInventoryAvailability || undefined,
    });
    return getRequirementQuotationValidationFlags(start, end, sellerCtx, buyerCtxForVal);
  }, [rangeValue, listingForm, listingInventoryAvailability, buyerCtxForVal]);

  const timelineLine = useMemo(() => {
    const pair = Array.isArray(rangeValue) ? rangeValue : null;
    if (!pair?.[0] || !pair?.[1] || !listingForm) return '';
    return buildQuotationTimelineLine(pair[0], pair[1], listingForm, buyerRequirementForm || {});
  }, [rangeValue, listingForm, buyerRequirementForm]);

  const leaseDurationSummary = useMemo(() => {
    const pair = Array.isArray(rangeValue) ? rangeValue : null;
    if (!pair?.[0]?.isValid?.() || !pair?.[1]?.isValid?.()) return '—';
    const u = inferLeaseUnitsFromRange(pair[0], pair[1], sellerCtxForLease.durationType);
    const unit = durationTypeLabel(sellerCtxForLease.durationType).toLowerCase();
    return `${u != null ? u : '—'} ${unit} · ${pair[0].format('YYYY-MM-DD')} → ${pair[1].format('YYYY-MM-DD')}`;
  }, [rangeValue, sellerCtxForLease.durationType]);

  const buyerReady = Boolean(!buyerRequirementLoading && buyerRequirementForm && !buyerRequirementError);
  const quotedPriceForApi = `${str(quotedAmount)} ${str(currency) || 'INR'}`.trim();
  const canSubmit =
    Boolean(str(quotedAmount)) &&
    Boolean(str(messageToBuyer)) &&
    selectionCheck.ok &&
    validationFlags.allOk &&
    buyerReady &&
    !listingError &&
    !listingLoading &&
    !uploadBusy;

  const lidNum = listingId == null ? NaN : Number(listingId);
  const eidNum = enquiryId == null ? NaN : Number(enquiryId);
  const canRejectRequest =
    Number.isFinite(lidNum) && lidNum >= 1 && Number.isFinite(eidNum) && eidNum >= 1 && !submitting;

  const uploadCompanyFolder = () => {
    const t = listingTitleDisplay || 'InfrastructureListing';
    return t.replace(/\s+/g, '_').slice(0, 80) || 'InfrastructureListing';
  };

  const handleQuotationFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'company');
      fd.append('companyName', uploadCompanyFolder());
      fd.append('fileType', 'document');
      const res = await CommonService.post('/upload/single', fd);
      if (!res?.success || !res?.data?.accessPath) {
        throw new Error(res?.message || 'Upload failed');
      }
      setQuotationDocPath(res.data.accessPath);
      setQuotationDocName(file.name);
      toast.success('Document attached.');
    } catch (err) {
      toast.error(err?.message || 'Could not upload file.');
    } finally {
      setUploadBusy(false);
    }
  };

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lid = listingId == null ? NaN : Number(listingId);
    const eid = enquiryId == null ? NaN : Number(enquiryId);
    if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
      toast.error('Invalid listing or enquiry.');
      return;
    }
    if (!str(quotedAmount)) {
      toast.error('Quoted amount is required.');
      return;
    }
    if (!str(messageToBuyer)) {
      toast.error('Message to buyer is required.');
      return;
    }
    if (!canSubmit) {
      toast.error('Complete mandatory fields and fix calendar validation before submitting.');
      return;
    }
    if (!timelineLine) {
      toast.error('Select valid lease dates.');
      return;
    }
    const quotationExtended = {
      currency: str(currency) || 'INR',
      quotedAmount: str(quotedAmount),
      paymentTerms: str(paymentTerms) || null,
      securityDeposit: str(securityDeposit) || null,
      additionalCharges: str(additionalCharges) || null,
      termsAndConditions: str(termsAndConditions) || null,
      quotationDocumentPath: quotationDocPath || null,
      quotationDocumentName: quotationDocName || null,
    };

    setSubmitting(true);
    try {
      const res = await postSellerEnquiryRequirementQuotation(lid, eid, {
        quotedPrice: quotedPriceForApi,
        timelineAvailability: timelineLine,
        additionalNotes: str(messageToBuyer),
        quotationExtended,
      });
      if (res?.success === false) {
        toast.error(res?.message || 'Could not save quotation.');
        return;
      }
      toast.success('Quotation saved.');
      dispatchInfrastructureNotificationsRefresh();
      onSuccess?.(res?.data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not save quotation.');
    } finally {
      setSubmitting(false);
    }
  };

  const titleId = `ile-req-quote-title-${suffix}`;
  const rejectTitleId = `ile-req-reject-title-${suffix}`;

  const handleConfirmRejectEnquiry = async () => {
    const lid = listingId == null ? NaN : Number(listingId);
    const eid = enquiryId == null ? NaN : Number(enquiryId);
    if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
      toast.error('Invalid listing or enquiry.');
      return;
    }
    const r = str(rejectReason);
    if (r.length < 3) {
      toast.warning('Enter a reason for rejection (at least 3 characters).');
      return;
    }
    setRejectSubmitting(true);
    try {
      const res = await rejectInfrastructureListingEnquiry(lid, eid, {
        reason: r,
        comments: str(rejectComments) || null,
      });
      if (res?.success === false) {
        toast.error(res?.message || 'Could not reject enquiry.');
        return;
      }
      toast.success(res?.message || 'Enquiry rejected.');
      dispatchInfrastructureNotificationsRefresh();
      setRejectModalOpen(false);
      onClose?.();
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not reject enquiry.');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const sellerWindowLabel = listingForm
    ? `${sellerCtxForLease.availableFrom.format('YYYY-MM-DD')} → ${sellerCtxForLease.windowEndInclusive.format('YYYY-MM-DD')}`
    : '—';

  const buyerChecksUnknown = Boolean(buyerRequirementError || buyerRequirementLoading);
  const buyerTimelineDisplayOk = !buyerChecksUnknown && validationFlags.buyerTimelineOk;
  const buyerDurationDisplayOk = !buyerChecksUnknown && validationFlags.buyerDurationRangeOk;

  const modalUi = (
    <div className="ile-modal-root">
      <div
        className="ile-modal-overlay"
        onClick={() => !submitting && !rejectModalOpen && !rejectSubmitting && onClose()}
        role="presentation"
      >
        <div
          className="ile-modal-panel ile-modal-panel--wide ile-modal-panel--xlarge"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          onClick={(ev) => ev.stopPropagation()}
        >
          <div className="ile-modal-panel__header">
            <button
              type="button"
              className="ile-modal-panel__close"
              onClick={() => !submitting && !rejectModalOpen && !rejectSubmitting && onClose()}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            <h2 className="ile-modal-panel__title" id={titleId}>
              Submit quotation
            </h2>
            <p className="ile-modal-panel__subtitle mb-0">
              Listing #{listingId} · Enquiry #{enquiryId}
              {buyerName ? ` · ${buyerName}` : ''}
            </p>
          </div>

          <form className="ile-modal-panel__form" onSubmit={handleSubmit} noValidate>
            <div className="ile-modal-panel__body">
              <div className="ile-modal-block mb-3">
                <h3 className="ile-modal-block__title">1 · Basic info (read-only)</h3>
                <div className="row">
                  <InfoItem label="Requirement ID" value={requirementIdDisplay} />
                  <InfoItem label="Listing / asset name" value={listingTitleDisplay || '—'} />
                  <InfoItem label="Buyer name" value={buyerName || '—'} />
                  <InfoItem label="Location" value={locationDisplay} />
                  <InfoItem label="Asset type / category" value={assetTypeDisplay} />
                </div>
              </div>

              <div className="ile-modal-block mb-3">
                <h3 className="ile-modal-block__title">2 · Availability calendar (mandatory)</h3>
                <p className="small text-muted mb-2">
                  Seller window: <strong>{sellerWindowLabel}</strong>. Select lease start; lease length (below calendar)
                  updates the end date automatically. Disabled dates are not available.
                </p>
                <InfrastructureLeasingListingAvailabilityStep
                  listingForm={listingForm}
                  listingInventoryAvailability={listingInventoryAvailability}
                  listingLoading={listingLoading}
                  listingError={listingError}
                  buyerForm={buyerRequirementForm}
                  buyerLoading={buyerRequirementLoading}
                  buyerError={buyerRequirementError}
                  rangeValue={rangeValue}
                  onRangeChange={setRangeValue}
                />
              </div>

              <div className="ile-modal-block mb-3">
                <h3 className="ile-modal-block__title">3 · Availability &amp; requirement summary</h3>
                <div className="row small">
                  <div className="col-md-6">
                    <div className="fw-semibold mb-1">Seller listing</div>
                    <dl className="row mb-0">
                      <dt className="col-6 text-muted">Duration type</dt>
                      <dd className="col-6">{listingForm ? durationTypeLabel(sellerCtxForLease.durationType) : '—'}</dd>
                      <dt className="col-6 text-muted">Available from</dt>
                      <dd className="col-6">{listingForm ? sellerCtxForLease.availableFrom.format('YYYY-MM-DD') : '—'}</dd>
                      <dt className="col-6 text-muted">Available till</dt>
                      <dd className="col-6">{listingForm ? sellerCtxForLease.windowEndInclusive.format('YYYY-MM-DD') : '—'}</dd>
                      <dt className="col-6 text-muted">Total duration</dt>
                      <dd className="col-6">
                        {listingForm
                          ? `${sellerCtxForLease.totalUnits} ${durationTypeLabel(sellerCtxForLease.durationType).toLowerCase()}`
                          : '—'}
                      </dd>
                    </dl>
                  </div>
                  <div className="col-md-6">
                    <div className="fw-semibold mb-1">Buyer requirement</div>
                    {buyerRequirementError ? (
                      <p className="text-danger mb-0 small">{buyerRequirementError}</p>
                    ) : (
                      <dl className="row mb-0">
                        <dt className="col-6 text-muted">Required by</dt>
                        <dd className="col-6">
                          {buyerCtxForVal.requiredBy ? buyerCtxForVal.requiredBy.format('YYYY-MM-DD') : '—'}
                        </dd>
                        <dt className="col-6 text-muted">Min duration</dt>
                        <dd className="col-6">
                          {buyerCtxForVal.minDuration != null
                            ? `${buyerCtxForVal.minDuration} ${durationTypeLabel(buyerCtxForVal.durationType).toLowerCase()}`
                            : '—'}
                        </dd>
                        <dt className="col-6 text-muted">Max duration</dt>
                        <dd className="col-6">
                          {buyerCtxForVal.maxDuration != null
                            ? `${buyerCtxForVal.maxDuration} ${durationTypeLabel(buyerCtxForVal.durationType).toLowerCase()}`
                            : '—'}
                        </dd>
                      </dl>
                    )}
                  </div>
                </div>
              </div>

              <div className="ile-modal-block mb-3">
                <h3 className="ile-modal-block__title">4 · Validation indicators (real-time)</h3>
                {buyerRequirementError ? (
                  <p className="small text-danger mb-2">{buyerRequirementError}</p>
                ) : null}
                <ul className="list-unstyled small mb-2">
                  <li className="mb-1">
                    {validationFlags.sellerAvailabilityOk ? '✅' : '❌'} Within seller availability
                  </li>
                  <li className="mb-1">
                    {buyerTimelineDisplayOk ? '✅' : '❌'} Matches buyer required timeline
                  </li>
                  <li className="mb-1">
                    {buyerDurationDisplayOk ? '✅' : '❌'} Duration within buyer min/max range
                  </li>
                </ul>
                {!validationFlags.allOk && validationFlags.warningMessages?.length ? (
                  <div className="alert alert-warning py-2 small mb-0" role="status">
                    <strong>Warnings</strong>
                    <ul className="mb-0 ps-3 mt-1">
                      {validationFlags.warningMessages.map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {selectionCheck.warnings?.length ? (
                  <ul className="small text-warning-emphasis mb-0 ps-3 mt-2">
                    {selectionCheck.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <div className="ile-modal-block mb-3">
                <h3 className="ile-modal-block__title">5 · Quotation details</h3>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="ile-form-label" htmlFor={`ile-req-amt-${suffix}`}>
                      Quoted amount <span className="text-danger">*</span>
                    </label>
                    <input
                      id={`ile-req-amt-${suffix}`}
                      type="text"
                      className="input-pill"
                      value={quotedAmount}
                      onChange={(ev) => setQuotedAmount(ev.target.value)}
                      placeholder="Amount"
                      autoComplete="off"
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="ile-form-label" htmlFor={`ile-req-cur-${suffix}`}>
                      Currency
                    </label>
                    <select
                      id={`ile-req-cur-${suffix}`}
                      className="input-pill"
                      value={
                        currencySelectList.some((c) => c.value === currency)
                          ? currency
                          : str(currency)
                            ? currency
                            : ''
                      }
                      onChange={(ev) => setCurrency(ev.target.value)}
                      disabled={currencyMasterLoading}
                    >
                      <option value="">{currencyMasterLoading ? 'Loading currencies…' : 'Select currency'}</option>
                      {currency && !currencySelectList.some((c) => c.value === currency) ? (
                        <option value={currency}>
                          {currency} (saved)
                        </option>
                      ) : null}
                      {currencySelectList.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="ile-form-label">Lease duration</label>
                    <div className="input-pill bg-light small mb-0 py-2">{leaseDurationSummary}</div>
                    <p className="ile-modal-field-hint mb-0">Adjust using the calendar section (lease length control).</p>
                  </div>
                </div>
              </div>

              <div className="ile-modal-block mb-3">
                <h3 className="ile-modal-block__title">6 · Commercial terms</h3>
                <div className="mb-2">
                  <label className="ile-form-label" htmlFor={`ile-req-pay-master-${suffix}`}>
                    Payment terms (master)
                  </label>
                  <select
                    id={`ile-req-pay-master-${suffix}`}
                    className="input-pill mb-2"
                    value={paymentTermsMatchesMaster}
                    disabled={paymentTermsMasterLoading}
                    onChange={(ev) => {
                      const v = ev.target.value;
                      if (v) setPaymentTerms(v);
                    }}
                  >
                    <option value="">
                      {paymentTermsMasterLoading ? 'Loading payment terms…' : 'Select payment schedule'}
                    </option>
                    {(paymentScheduleOptions || []).map((ps, idx) => (
                      <option key={`${String(ps.value)}-${idx}`} value={str(ps.label)}>
                        {str(ps.label)}
                      </option>
                    ))}
                  </select>
                  <label className="ile-form-label" htmlFor={`ile-req-pay-${suffix}`}>
                    Payment terms (detail)
                  </label>
                  <textarea
                    id={`ile-req-pay-${suffix}`}
                    className="input-pill"
                    rows={2}
                    value={paymentTerms}
                    onChange={(ev) => setPaymentTerms(ev.target.value)}
                    placeholder="Choose a schedule above or type custom payment terms"
                  />
                </div>
                <div className="mb-2">
                  <label className="ile-form-label" htmlFor={`ile-req-dep-${suffix}`}>
                    Security deposit
                  </label>
                  <input
                    id={`ile-req-dep-${suffix}`}
                    type="text"
                    className="input-pill"
                    value={securityDeposit}
                    onChange={(ev) => setSecurityDeposit(ev.target.value)}
                    placeholder="e.g. 2 months rent"
                  />
                </div>
                <div className="mb-0">
                  <label className="ile-form-label" htmlFor={`ile-req-chg-${suffix}`}>
                    Additional charges (optional)
                  </label>
                  <textarea
                    id={`ile-req-chg-${suffix}`}
                    className="input-pill"
                    rows={2}
                    value={additionalCharges}
                    onChange={(ev) => setAdditionalCharges(ev.target.value)}
                    placeholder="CAM, utilities, insurance, etc."
                  />
                </div>
              </div>

              <div className="ile-modal-block mb-3">
                <h3 className="ile-modal-block__title">7 · Notes / message</h3>
                <div className="mb-2">
                  <label className="ile-form-label" htmlFor={`ile-req-msg-${suffix}`}>
                    Message to buyer <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id={`ile-req-msg-${suffix}`}
                    className="input-pill"
                    rows={3}
                    value={messageToBuyer}
                    onChange={(ev) => setMessageToBuyer(ev.target.value)}
                    placeholder="Cover letter or key points for the buyer"
                    required
                  />
                </div>
                <div className="mb-0">
                  <label className="ile-form-label" htmlFor={`ile-req-tc-${suffix}`}>
                    Terms &amp; conditions (optional)
                  </label>
                  <textarea
                    id={`ile-req-tc-${suffix}`}
                    className="input-pill"
                    rows={3}
                    value={termsAndConditions}
                    onChange={(ev) => setTermsAndConditions(ev.target.value)}
                    placeholder="Optional legal or commercial clauses"
                  />
                </div>
              </div>

              <div className="ile-modal-block mb-3">
                <h3 className="ile-modal-block__title">8 · Attachment (optional)</h3>
                <input
                  type="file"
                  className="form-control form-control-sm"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  disabled={uploadBusy || submitting}
                  onChange={handleQuotationFile}
                />
                {quotationDocName ? (
                  <p className="small text-muted mb-0 mt-2">
                    Attached: <strong>{quotationDocName}</strong>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 ms-2"
                      onClick={() => {
                        setQuotationDocPath(null);
                        setQuotationDocName(null);
                      }}
                    >
                      Remove
                    </button>
                  </p>
                ) : (
                  <p className="small text-muted mb-0 mt-1">Upload a quotation PDF or image (optional).</p>
                )}
              </div>

              <div className="ile-modal-block mb-0">
                <h3 className="ile-modal-block__title">Timeline summary (auto)</h3>
                <textarea
                  className="input-pill ile-readonly-message"
                  rows={3}
                  readOnly
                  value={timelineLine}
                  placeholder={listingLoading || buyerRequirementLoading ? 'Loading…' : 'Select dates in the calendar'}
                />
              </div>
            </div>
            <div className="ile-modal-panel__footer">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm me-auto"
                onClick={() => onClose?.()}
                disabled={submitting || rejectModalOpen || rejectSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                disabled={!canRejectRequest || rejectModalOpen || rejectSubmitting}
                onClick={() => setRejectModalOpen(true)}
              >
                Reject request
              </button>
              <button type="submit" className="ile-modal-btn-primary" disabled={submitting || rejectSubmitting || !canSubmit}>
                {submitting ? 'Saving…' : 'Submit quotation'}
              </button>
            </div>
          </form>

          {rejectModalOpen ? (
            <div
              className="ile-req-reject-layer"
              role="presentation"
              onClick={() => !rejectSubmitting && setRejectModalOpen(false)}
            >
              <div
                className="ile-req-reject-card"
                role="dialog"
                aria-modal="true"
                aria-labelledby={rejectTitleId}
                onClick={(ev) => ev.stopPropagation()}
              >
                <h3 className="h6 fw-bold mb-2" id={rejectTitleId}>
                  Reject enquiry
                </h3>
                <p className="small text-muted mb-3 mb-0 pb-3 border-bottom">
                  Decline this buyer&apos;s request for{' '}
                  <strong>{listingTitleDisplay || 'this listing'}</strong>
                  {buyerName ? (
                    <>
                      {' '}
                      · <strong>{buyerName}</strong>
                    </>
                  ) : null}
                  . This is not the same as cancelling an accepted deal — it only rejects this enquiry while it is still
                  open.
                </p>
                <div className="pt-3">
                  <label className="ile-form-label" htmlFor={`ile-req-rej-reason-${suffix}`}>
                    Reason for rejection <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id={`ile-req-rej-reason-${suffix}`}
                    className="input-pill"
                    style={{ minHeight: 88, resize: 'vertical' }}
                    value={rejectReason}
                    onChange={(ev) => setRejectReason(ev.target.value)}
                    maxLength={512}
                    disabled={rejectSubmitting}
                  />
                  <label className="ile-form-label mt-2" htmlFor={`ile-req-rej-comments-${suffix}`}>
                    Additional comments (optional)
                  </label>
                  <textarea
                    id={`ile-req-rej-comments-${suffix}`}
                    className="input-pill"
                    style={{ minHeight: 72, resize: 'vertical' }}
                    value={rejectComments}
                    onChange={(ev) => setRejectComments(ev.target.value)}
                    maxLength={4000}
                    disabled={rejectSubmitting}
                  />
                </div>
                <div className="d-flex justify-content-end gap-2 mt-3 pt-2 border-top">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={rejectSubmitting}
                    onClick={() => setRejectModalOpen(false)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    disabled={rejectSubmitting}
                    onClick={handleConfirmRejectEnquiry}
                  >
                    {rejectSubmitting ? 'Rejecting…' : 'Confirm reject'}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(modalUi, document.body);
}
